import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Link2, Loader2, Check, AlertTriangle, Info, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { motifVars } from '../../../data/motifs';
import { useDashboard } from './dashboardContext';

const TYPE_LV = {
  reservations: 'Rezervācijas', menu: 'Ēdienkarte', business_card: 'Vizītkarte',
};
const KIND_LV = {
  general: 'Vispārīga', salon: 'Salons / skaistumkopšana', health: 'Veselība', fitness: 'Fitness / sports',
  auto: 'Auto serviss', education: 'Kursi / izglītība', stay: 'Naktsmītne', trade: 'Meistars / amatnieks',
  realestate: 'Nekustamais īpašums', special: 'Special (pielāgojama)',
};

export default function GenerateFromUrl() {
  const { API, reloadPages, selectPage } = useDashboard();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { page, log }

  const reset = () => { setUrl(''); setBusy(false); setResult(null); };

  const generate = async () => {
    const u = url.trim();
    if (!u) { toast.error('Ielīmē mājaslapas saiti'); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/business-page/me/generate-from-url`, { url: u }, { timeout: 120000 });
      await reloadPages();
      setResult(data);
      toast.success('Lapa izģenerēta! Pārskati un publicē.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Neizdevās izģenerēt lapu. Pārbaudi saiti vai mēģini citu.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const openPage = () => {
    const page = result?.page;
    setOpen(false);
    reset();
    if (page) selectPage(page);
  };

  const log = result?.log || {};

  return (
    <>
      <motion.button
        whileHover={{ y: -2 }}
        onClick={() => { reset(); setOpen(true); }}
        className="w-full mb-6 rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/[0.08] hover:bg-[var(--accent)]/[0.14] transition-colors p-4 sm:p-5 flex items-center gap-4 text-left"
        data-testid="generate-from-url-trigger">
        <span className="w-11 h-11 rounded-xl bg-[var(--accent)] text-[var(--accent-contrast)] flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-white font-semibold text-sm sm:text-base" style={{ fontFamily: 'Outfit' }}>Auto-ģenerēt no saites</span>
          <span className="block text-white/50 text-xs sm:text-sm mt-0.5">Ielīmē esošas mājaslapas saiti — izveidosim tavu lapu pēc tās parauga, automātiski izvēloties veidu un saturu.</span>
        </span>
        <ArrowRight className="w-5 h-5 text-[var(--accent)] shrink-0" />
      </motion.button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg bg-[#12131a] border-white/10 text-white" style={motifVars('amber_warmth')} data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white" style={{ fontFamily: 'Outfit' }}>
              <Sparkles className="w-5 h-5 text-[var(--accent)]" /> Ģenerēt lapu no saites
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Ievadi jebkuras mājaslapas adresi. Mēs to izanalizēsim un izveidosim jaunu melnraksta lapu ar pārņemto saturu.
            </DialogDescription>
          </DialogHeader>

          {!result && (
            <div className="space-y-4">
              <div className="relative">
                <Link2 className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !busy) generate(); }}
                  placeholder="piemēram.lv"
                  disabled={busy}
                  className="w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--accent)]/60 disabled:opacity-50"
                  data-testid="generate-url-input" />
              </div>

              {busy ? (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-3" data-testid="generate-progress">
                  <Loader2 className="w-5 h-5 text-[var(--accent)] animate-spin shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">Analizējam lapu…</p>
                    <p className="text-white/45 text-xs mt-0.5">Nolasām saturu, nosakām lapas veidu un pārņemam cenas, logo un kontaktus. Tas var aizņemt līdz minūtei.</p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={generate}
                  className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold text-sm hover:bg-[var(--accent-hover)] transition-colors flex items-center justify-center gap-2"
                  data-testid="generate-submit">
                  <Sparkles className="w-4 h-4" /> Ģenerēt lapu
                </button>
              )}
              <p className="text-white/30 text-[11px] leading-relaxed">
                Lapa tiks izveidota kā <span className="text-white/50">pauzēts melnraksts</span> — vari to pārskatīt, papildināt un tad publicēt. Netiek mainīta neviena esošā lapa.
              </p>
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="generate-result">
              <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/[0.07] p-4 flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-emerald-400/15 text-emerald-300 flex items-center justify-center shrink-0"><Check className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{result.page?.display_name || 'Jauna lapa'}</p>
                  <p className="text-white/50 text-xs mt-0.5">
                    {TYPE_LV[result.page?.template] || result.page?.template}
                    {result.page?.template === 'reservations' && result.page?.kind ? ` · ${KIND_LV[result.page.kind] || result.page.kind}` : ''}
                    {' · '}Melnraksts (pauzēts)
                  </p>
                </div>
              </div>

              {log.source && (
                <p className="text-white/35 text-[11px] break-all">Avots: {log.source}</p>
              )}

              {Array.isArray(log.used) && log.used.length > 0 && (
                <div data-testid="log-used">
                  <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Pārņemts ({log.used.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {log.used.map((x, i) => (
                      <span key={i} className="text-[11px] bg-white/[0.06] text-white/70 px-2 py-0.5 rounded-full">{x}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(log.missing) && log.missing.length > 0 && (
                <div data-testid="log-missing">
                  <p className="text-orange-300 text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Nav atrasts / jāpapildina ({log.missing.length})</p>
                  <ul className="space-y-1">
                    {log.missing.map((x, i) => (
                      <li key={i} className="text-white/55 text-xs flex items-start gap-1.5"><span className="text-orange-300/60 mt-0.5">•</span>{x}</li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(log.notes) && log.notes.length > 0 && (
                <div data-testid="log-notes">
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Piezīmes</p>
                  <ul className="space-y-1">
                    {log.notes.map((x, i) => (
                      <li key={i} className="text-white/45 text-xs flex items-start gap-1.5"><span className="text-white/30 mt-0.5">•</span>{x}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={openPage}
                  className="flex-1 h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold text-sm hover:bg-[var(--accent-hover)] transition-colors flex items-center justify-center gap-2"
                  data-testid="generate-open-page">
                  Atvērt un rediģēt <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={reset}
                  className="h-11 px-4 rounded-full border border-white/15 text-white/70 text-sm font-semibold hover:bg-white/5 transition-colors flex items-center gap-1.5"
                  data-testid="generate-another">
                  <X className="w-4 h-4" /> Vēl vienu
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
